import { useCallback, useEffect, useMemo, useState } from 'react'
import { notification } from 'antd'
import isNil from 'lodash/isNil'
import omit from 'lodash/omit'
import { useHubspotSyncMutation } from '../../api/client-billing-profile-api-client'
import { extractError } from '../../api/extract-error'
import type { HubspotSyncNewItemDto } from '../../api/dto/client-billing-profile.dto'
import { useFundingInviteAccess } from '../UserManagement/useFundingInviteAccess'
import { useAccessGrantRun } from './useAccessGrantRun'
import {
  buildCreateUpdates,
  buildNewCompanyHint,
  grantEmails,
  submitFailureReason,
  type NewCompanyRow,
} from './hubspotSync.helpers'

interface UseHubspotSyncNewCompaniesParams {
  items: HubspotSyncNewItemDto[]
  /** Identifies the run these companies came from; a new one clears old edits. */
  runId: number
  onSubmitted: (hubspotCompanyIds: string[]) => void
}

const grantKey = (hubspotCompanyId: string, email: string) =>
  `${hubspotCompanyId}:${email.toLowerCase()}`

/**
 * The new companies listed in the sync banner: what the operator fills in per
 * company, and the submit that creates one profile and then grants access to
 * the addresses they opted in.
 *
 * The two halves are reported separately because they fail differently: the
 * profile write is all-or-nothing across the batch, while the access grant is
 * isolated per recipient.
 */
export const useHubspotSyncNewCompanies = ({
  items,
  runId,
  onSubmitted,
}: UseHubspotSyncNewCompaniesParams) => {
  const [paymentTypeByCompany, setPaymentTypeByCompany] = useState<
    Record<string, string>
  >({})
  const [dueDayByCompany, setDueDayByCompany] = useState<
    Record<string, number | undefined>
  >({})
  const [emailsByCompany, setEmailsByCompany] = useState<
    Record<string, string[]>
  >({})
  const [grantByEmail, setGrantByEmail] = useState<Record<string, boolean>>({})
  const [submitErrors, setSubmitErrors] = useState<Record<string, string>>({})
  // Which company's submit is in flight, so only that row shows a spinner.
  const [submittingId, setSubmittingId] = useState<string | null>(null)

  // An override only ever describes the run it was typed against: a company
  // reappearing in a later run must come back with HubSpot's own values.
  useEffect(() => {
    setPaymentTypeByCompany({})
    setDueDayByCompany({})
    setEmailsByCompany({})
    setGrantByEmail({})
    setSubmitErrors({})
    setSubmittingId(null)
  }, [runId])

  const inviteAccess = useFundingInviteAccess()
  const syncMutation = useHubspotSyncMutation()
  const accessGrants = useAccessGrantRun(
    'The profile was created, but granting access failed'
  )

  // Values fall back to what HubSpot returned rather than being seeded into
  // state, so a fresh run can never show a previous run's answers.
  const rows = useMemo<NewCompanyRow[]>(
    () =>
      items.map((item) => {
        const id = item.hubspotCompanyId
        const paymentType =
          paymentTypeByCompany[id] ?? item.paymentType ?? undefined
        const dueDay =
          id in dueDayByCompany
            ? dueDayByCompany[id]
            : (item.dueDay ?? undefined)
        const emails = emailsByCompany[id] ?? item.hubspotContactEmails
        return {
          item,
          paymentType,
          dueDay,
          emails,
          grantedEmails: inviteAccess.allowed
            ? emails.filter(
                (email) => grantByEmail[grantKey(id, email)] ?? true
              )
            : [],
          isReady: Boolean(paymentType) && !isNil(dueDay),
          submitError: submitErrors[id] ?? null,
        }
      }),
    [
      items,
      paymentTypeByCompany,
      dueDayByCompany,
      emailsByCompany,
      grantByEmail,
      inviteAccess.allowed,
      submitErrors,
    ]
  )

  const startGrants = useCallback(
    (row: NewCompanyRow) => {
      // Only a company with a resolved client can be granted against, and it is
      // the same condition that let the company into this modal.
      const { clientPublicKey, hubspotCompanyName } = row.item
      const grants = isNil(clientPublicKey)
        ? []
        : grantEmails(row.grantedEmails).map((email) => ({
            email,
            personId: null,
            clientPublicKeys: [clientPublicKey],
            roleIds: inviteAccess.roleIds,
          }))
      accessGrants.grantAccess(
        `Billing profile created for ${hubspotCompanyName}.`,
        grants
      )
    },
    [accessGrants.grantAccess, inviteAccess.roleIds]
  )

  const handleSubmitCompany = useCallback(
    (hubspotCompanyId: string) => {
      const row = rows.find(
        (candidate) => candidate.item.hubspotCompanyId === hubspotCompanyId
      )
      if (isNil(row) || !row.isReady || !isNil(submittingId)) return

      setSubmittingId(hubspotCompanyId)
      syncMutation.mutate(
        { updates: buildCreateUpdates([row]) },
        {
          // A 2xx can still refuse the row. It then stays in the banner with
          // the server's reason and whatever the operator already filled in.
          onSuccess: (result) => {
            setSubmittingId(null)
            const error = submitFailureReason(result?.failed ?? [], {
              clientBillingProfileId: null,
              clientPublicKey: row.item.clientPublicKey,
            })
            const { hubspotCompanyName } = row.item

            if (!isNil(error)) {
              setSubmitErrors((curr) => ({
                ...curr,
                [hubspotCompanyId]: error,
              }))
              notification.warning({
                message: `${hubspotCompanyName} could not be created`,
              })
              return
            }

            setSubmitErrors((curr) => omit(curr, hubspotCompanyId))
            notification.success({
              message: `Billing profile created for ${hubspotCompanyName}`,
            })
            onSubmitted([hubspotCompanyId])
            startGrants(row)
          },
          onError: (error) => {
            setSubmittingId(null)
            notification.error({
              message: extractError(
                error,
                'Failed to create the billing profile'
              ),
            })
          },
        }
      )
    },
    [rows, submittingId, syncMutation.mutate, onSubmitted, startGrants]
  )

  return {
    rows,
    accessGrants,
    canGrant: inviteAccess.allowed,
    hint: buildNewCompanyHint({
      total: rows.length,
      readyCount: rows.filter((row) => row.isReady).length,
    }),
    isSubmittingCompany: (companyId: string) => submittingId === companyId,
    /** One write at a time, so the other rows' buttons have to go quiet. */
    isSubmittingAny: !isNil(submittingId),
    setPaymentType: (companyId: string, value: string) =>
      setPaymentTypeByCompany((curr) => ({ ...curr, [companyId]: value })),
    setDueDay: (companyId: string, value?: number) =>
      setDueDayByCompany((curr) => ({ ...curr, [companyId]: value })),
    setEmails: (companyId: string, emails: string[]) =>
      setEmailsByCompany((curr) => ({ ...curr, [companyId]: emails })),
    setGranted: (companyId: string, email: string, granted: boolean) =>
      setGrantByEmail((curr) => ({
        ...curr,
        [grantKey(companyId, email)]: granted,
      })),
    isGranted: (companyId: string, email: string) =>
      grantByEmail[grantKey(companyId, email)] ?? true,
    handleSubmitCompany,
  }
}
